import { useCallback, useEffect, useRef, useState } from 'react';
import {
    PlusIcon,
    MinusIcon,
    Squares2X2Icon,
    ArrowsUpDownIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import {
    MIN_COLS,
    MAX_COLS,
    MIN_ROW_HEIGHT,
    MAX_ROW_HEIGHT,
    HEIGHT_S,
    HEIGHT_M,
    HEIGHT_L,
    PRESETS,
    type GridAttrs,
    type Preset
} from './imageGridShared';

// ── PresetThumb ───────────────────────────────────────────────────────────────

interface PresetThumbProps {
    preset: Preset;
    onSelect: (preset: Preset) => void;
}

function PresetThumb({ preset, onSelect }: PresetThumbProps) {
    const cellW = Math.floor(36 / preset.cols);
    const cellH = Math.floor(24 / preset.rows);

    return (
        <button
            type="button"
            className="image-grid-preset-thumb"
            title={preset.label}
            onClick={() => onSelect(preset)}
        >
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${preset.cols}, 1fr)`,
                    gridTemplateRows: `repeat(${preset.rows}, 1fr)`,
                    gap: 2,
                    width: cellW * preset.cols + 2 * (preset.cols - 1),
                    height: cellH * preset.rows + 2 * (preset.rows - 1)
                }}
            >
                {Array.from({ length: preset.rows * preset.cols }).map((_, i) => (
                    <div key={i} className="image-grid-preset-cell" />
                ))}
            </div>
            <span className="image-grid-preset-label">{preset.label}</span>
        </button>
    );
}

// ── ChipGroup ─────────────────────────────────────────────────────────────────

interface ChipGroupProps {
    rowHeight: number;
    onHeightChange: (h: number) => void;
}

function ChipGroup({ rowHeight, onHeightChange }: ChipGroupProps) {
    const [customOpen, setCustomOpen] = useState(false);
    const [customVal, setCustomVal] = useState(String(rowHeight));
    const customRef = useRef<HTMLDivElement>(null);

    const isS = rowHeight === HEIGHT_S;
    const isM = rowHeight === HEIGHT_M;
    const isL = rowHeight === HEIGHT_L;
    const isCustom = !isS && !isM && !isL;

    useEffect(() => {
        if (!customOpen) return;
        const handleOutside = (e: MouseEvent) => {
            if (customRef.current && !customRef.current.contains(e.target as Node)) {
                setCustomOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [customOpen]);

    const applyCustom = useCallback(() => {
        const parsed = parseInt(customVal, 10);
        if (!isNaN(parsed)) {
            onHeightChange(Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, parsed)));
        }
        setCustomOpen(false);
    }, [customVal, onHeightChange]);

    return (
        <div className="image-grid-chip-group" style={{ position: 'relative' }}>
            <ArrowsUpDownIcon width={13} height={13} style={{ color: '#9ca3af', flexShrink: 0 }} />
            {(
                [
                    { label: 'S', value: HEIGHT_S, active: isS },
                    { label: 'M', value: HEIGHT_M, active: isM },
                    { label: 'L', value: HEIGHT_L, active: isL }
                ] as const
            ).map(({ label, value, active }) => (
                <button
                    key={label}
                    type="button"
                    className={`image-grid-chip${active ? ' active' : ''}`}
                    title={`Row height: ${value}pt`}
                    onClick={() => onHeightChange(value)}
                >
                    {label}
                </button>
            ))}
            <div ref={customRef} style={{ position: 'relative' }}>
                <button
                    type="button"
                    className={`image-grid-chip${isCustom ? ' active' : ''}`}
                    title="Custom height"
                    onClick={() => setCustomOpen((v) => !v)}
                >
                    ⋯
                </button>
                {customOpen && (
                    <div className="image-grid-custom-popover">
                        <input
                            type="number"
                            min={MIN_ROW_HEIGHT}
                            max={MAX_ROW_HEIGHT}
                            value={customVal}
                            onChange={(e) => setCustomVal(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') applyCustom();
                            }}
                            className="image-grid-custom-input"
                            autoFocus
                        />
                        <span style={{ fontSize: 10, color: '#6b7280' }}>pt</span>
                        <button
                            type="button"
                            className="image-grid-custom-apply"
                            onClick={applyCustom}
                        >
                            Apply
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── GridToolbar ───────────────────────────────────────────────────────────────

interface GridToolbarProps {
    attrs: GridAttrs;
    onAddRow: () => void;
    onRemoveRow: () => void;
    onAddCol: () => void;
    onRemoveCol: () => void;
    onHeightChange: (h: number) => void;
    onPreset: (preset: Preset) => void;
    onDeleteGrid: () => void;
}

export function GridToolbar({
    attrs,
    onAddRow,
    onRemoveRow,
    onAddCol,
    onRemoveCol,
    onHeightChange,
    onPreset,
    onDeleteGrid
}: GridToolbarProps) {
    const [presetsOpen, setPresetsOpen] = useState(false);
    const presetsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!presetsOpen) return;
        const handleOutside = (e: MouseEvent) => {
            if (presetsRef.current && !presetsRef.current.contains(e.target as Node)) {
                setPresetsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [presetsOpen]);

    const handlePresetSelect = useCallback(
        (preset: Preset) => {
            setPresetsOpen(false);
            onPreset(preset);
        },
        [onPreset]
    );

    return (
        <div className="image-grid-toolbar">
            <div ref={presetsRef} style={{ position: 'relative' }}>
                <button
                    type="button"
                    className="image-grid-icon-btn"
                    title="Layout presets"
                    onClick={() => setPresetsOpen((v) => !v)}
                >
                    <Squares2X2Icon width={14} height={14} />
                </button>
                {presetsOpen && (
                    <div className="image-grid-presets-popover">
                        {PRESETS.map((p) => (
                            <PresetThumb key={p.label} preset={p} onSelect={handlePresetSelect} />
                        ))}
                    </div>
                )}
            </div>

            <div className="image-grid-divider" />

            <button
                type="button"
                className="image-grid-icon-btn"
                title="Add row"
                onClick={onAddRow}
            >
                <PlusIcon width={14} height={14} />
                <span className="image-grid-btn-label">Row</span>
            </button>
            <button
                type="button"
                className="image-grid-icon-btn"
                title="Remove row"
                disabled={attrs.rows <= 1}
                onClick={onRemoveRow}
            >
                <MinusIcon width={14} height={14} />
                <span className="image-grid-btn-label">Row</span>
            </button>

            <div className="image-grid-divider" />

            <button
                type="button"
                className="image-grid-icon-btn"
                title="Add column"
                disabled={attrs.cols >= MAX_COLS}
                onClick={onAddCol}
            >
                <PlusIcon width={14} height={14} />
                <span className="image-grid-btn-label">Col</span>
            </button>
            <button
                type="button"
                className="image-grid-icon-btn"
                title="Remove column"
                disabled={attrs.cols <= MIN_COLS}
                onClick={onRemoveCol}
            >
                <MinusIcon width={14} height={14} />
                <span className="image-grid-btn-label">Col</span>
            </button>

            <div className="image-grid-divider" />

            <ChipGroup rowHeight={attrs.rowHeight} onHeightChange={onHeightChange} />

            <div className="image-grid-divider" />

            <button
                type="button"
                className="image-grid-icon-btn is-destructive"
                title="Delete grid"
                aria-label="Delete grid"
                onClick={onDeleteGrid}
            >
                <TrashIcon width={14} height={14} />
            </button>
        </div>
    );
}
